import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import config from '../../utils/config.js';
import fullLogo from '../../assets/images/fullLogo.png';
import Notification from '../../components/Notification.js';
import { isValidRmaAttachmentFile } from '../../utils/rma-attachment-validation.js';
import { isValidEmailAddress } from '../../utils/email-validation.js';

const DETAILS_TEXTAREA_ROWS_COLLAPSED = 2;
const DETAILS_TEXTAREA_ROWS_EXPANDED = 8;

function contactToRmaForm(contact) {
    return {
        name: contact?.Name || '',
        phone: contact?.Phone || '',
        email: contact?.Email || '',
    };
}

export default function RmaEntry() {
    const ORIGIN = config.origin;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const attachmentInputRef = useRef(null);
    const attachmentPreviewUrlsRef = useRef(new Set());

    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [releaseNumber, setReleaseNumber] = useState(''); // Known as "Shipment Reference Number" to customers
    const [poNumber, setPoNumber] = useState('');
    const [viewState, setViewState] = useState('entry');
    const [errorNotificationVisible, setErrorNotificationVisible] = useState(false);
    const [errorNotificationTitle, setErrorNotificationTitle] = useState('');
    const [errorNotificationDesc, setErrorNotificationDesc] = useState('');
    const [orderDetails, setOrderDetails] = useState(null);
    const [invoiceLines, setInvoiceLines] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [rmaContact, setRmaContact] = useState({
        name: '',
        phone: '',
        email: '',
    });
    const [contactsOnAccount, setContactsOnAccount] = useState({});
    const [selectedContactId, setSelectedContactId] = useState('');
    const [lineReturnReasonOptions, setLineReturnReasonOptions] = useState([]);
    const [isLoadingValidation, setIsLoadingValidation] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [expandedDetailsLineId, setExpandedDetailsLineId] = useState(null);
    const [isCreateContactModalOpen, setIsCreateContactModalOpen] = useState(false);
    const [newContactForm, setNewContactForm] = useState({
        name: '',
        phone: '',
        email: '',
    });
    const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
    const [editContactForm, setEditContactForm] = useState({
        name: '',
        phone: '',
        email: '',
    });
    const [internalRmaOnly, setInternalRmaOnly] = useState(false);

    const errorString = 'Could not validate this request. Please try again.';
    const isInternalRmaEntry = searchParams.get('internal') === 'true';

    function navigateToRma() {
        // Persist initial internal RMA entry state in the URL
        navigate(isInternalRmaEntry ? '/rma?internal=true' : '/rma');
    }

    function handleRmaContactDropdownChange(event) {
        const nextContactId = event.target.value;
        setSelectedContactId(nextContactId);

        const selectedContactData = nextContactId ? contactsOnAccount[nextContactId] : null;
        if (selectedContactData) {
            setRmaContact(contactToRmaForm(selectedContactData));
        }
    }

    function updateNewContactField(fieldName, fieldValue) {
        setNewContactForm((previousNewContactForm) => ({
            ...previousNewContactForm,
            [fieldName]: fieldValue,
        }));
    }

    function openCreateContactModal() {
        setNewContactForm({
            name: '',
            phone: '',
            email: '',
        });
        setIsCreateContactModalOpen(true);
    }

    function closeCreateContactModal() {
        setIsCreateContactModalOpen(false);
    }

    function updateEditContactField(fieldName, fieldValue) {
        setEditContactForm((previousEditContactForm) => ({
            ...previousEditContactForm,
            [fieldName]: fieldValue,
        }));
    }

    function openEditContactModal() {
        if (!selectedContactId) return;
        const selectedContactData = contactsOnAccount[selectedContactId] || {};
        setEditContactForm({
            name: selectedContactData.Name || rmaContact.name || '',
            phone: selectedContactData.Phone || rmaContact.phone || '',
            email: selectedContactData.Email || rmaContact.email || '',
        });
        setIsEditContactModalOpen(true);
    }

    function closeEditContactModal() {
        setIsEditContactModalOpen(false);
    }

    function saveEditedContactFromModal() {
        const sanitizedName = editContactForm.name.trim();
        const sanitizedPhone = editContactForm.phone.trim();
        const sanitizedEmail = editContactForm.email.trim();

        if (!sanitizedName) {
            presentErrorNotification('Missing information', 'Contact name is required.');
            return;
        }

        const updatedContact = {
            Id: selectedContactId,
            Name: sanitizedName,
            Phone: sanitizedPhone,
            Email: sanitizedEmail,
        };

        if (selectedContactId) {
            setContactsOnAccount((previousContactsOnAccount) => ({
                ...previousContactsOnAccount,
                [selectedContactId]: updatedContact,
            }));
        }
        setRmaContact(contactToRmaForm(updatedContact));
        setIsEditContactModalOpen(false);
    }

    function createContactFromModal() {
        const sanitizedName = newContactForm.name.trim();
        const sanitizedPhone = newContactForm.phone.trim();
        const sanitizedEmail = newContactForm.email.trim();

        if (!sanitizedName) {
            presentErrorNotification('Missing information', 'Contact name is required.');
            return;
        }

        if (!sanitizedEmail) {
            presentErrorNotification('Missing information', 'Contact email is required.');
            return;
        }

        if (!isValidEmailAddress(sanitizedEmail)) {
            presentErrorNotification('Invalid email', 'Enter a valid email address.');
            return;
        }

        const normalizedEmail = sanitizedEmail.toLowerCase();
        const accountHasMatchingEmail = Object.values(contactsOnAccount).some((contactOption) => {
            const existingEmail = (contactOption?.Email || '').trim().toLowerCase();
            return existingEmail && existingEmail === normalizedEmail;
        });
        if (accountHasMatchingEmail) {
            presentErrorNotification('Duplicate email', 'That email already belongs to another contact on this account.');
            return;
        }

        const temporaryContactId = `new-contact-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const newContact = {
            Id: temporaryContactId,
            Name: sanitizedName,
            Phone: sanitizedPhone,
            Email: sanitizedEmail,
        };
        setContactsOnAccount((previousContactsOnAccount) => ({
            ...previousContactsOnAccount,
            [temporaryContactId]: newContact,
        }));
        setSelectedContactId(temporaryContactId);
        setRmaContact(contactToRmaForm(newContact));
        setIsCreateContactModalOpen(false);
    }

    function presentErrorNotification(notificationTitle, notificationDescription) {
        setErrorNotificationTitle(notificationTitle);
        setErrorNotificationDesc(notificationDescription);
        setErrorNotificationVisible(true);
    }

    function clearAttachments() {
        setAttachments((previousAttachments) => {
            previousAttachments.forEach((attachment) => {
                if (attachment.previewUrl) {
                    URL.revokeObjectURL(attachment.previewUrl);
                    attachmentPreviewUrlsRef.current.delete(attachment.previewUrl);
                }
            });
            return [];
        });
        if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }

    function handleAttachmentChange(event) {
        const selectedFiles = Array.from(event.target.files || []);
        const invalidFileCount = selectedFiles.filter((selectedFile) => !isValidRmaAttachmentFile(selectedFile)).length;
        const validFiles = selectedFiles.filter((selectedFile) => isValidRmaAttachmentFile(selectedFile));

        if (invalidFileCount > 0) {
            presentErrorNotification(
                'Invalid file type',
                'Only PDF, JPG, JPEG, and PNG files can be uploaded as attachments.'
            );
        }

        if (!validFiles.length) {
            if (attachmentInputRef.current) attachmentInputRef.current.value = '';
            return;
        }

        setAttachments((previousAttachments) => {
            const existingAttachmentKeys = new Set(
                previousAttachments.map(
                    (attachment) => `${attachment.file.name}-${attachment.file.size}-${attachment.file.lastModified}`
                )
            );

            const nextAttachments = [...previousAttachments];
            validFiles.forEach((validFile) => {
                const attachmentKey = `${validFile.name}-${validFile.size}-${validFile.lastModified}`;
                if (existingAttachmentKeys.has(attachmentKey)) return;

                const isImageAttachment = validFile.type.startsWith('image/');
                const previewUrl = isImageAttachment ? URL.createObjectURL(validFile) : '';
                if (previewUrl) attachmentPreviewUrlsRef.current.add(previewUrl);

                nextAttachments.push({
                    id: `${attachmentKey}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    file: validFile,
                    previewUrl,
                    isImageAttachment,
                });
            });

            return nextAttachments;
        });

        if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    }

    function removeAttachment(attachmentId) {
        setAttachments((previousAttachments) => {
            const attachmentToRemove = previousAttachments.find((attachment) => attachment.id === attachmentId);
            if (attachmentToRemove?.previewUrl) {
                URL.revokeObjectURL(attachmentToRemove.previewUrl);
                attachmentPreviewUrlsRef.current.delete(attachmentToRemove.previewUrl);
            }

            return previousAttachments.filter((attachment) => attachment.id !== attachmentId);
        });
    }

    useEffect(() => {
        return () => {
            attachmentPreviewUrlsRef.current.forEach((previewUrl) => {
                URL.revokeObjectURL(previewUrl);
            });
            attachmentPreviewUrlsRef.current.clear();
        };
    }, []);

    useEffect(() => {
        if (!expandedDetailsLineId) return;
        const line = invoiceLines.find((invoiceLine) => invoiceLine.invoiceLineId === expandedDetailsLineId);
        if (!line?.selected) {
            setExpandedDetailsLineId(null);
        }
    }, [invoiceLines, expandedDetailsLineId]);

    /** Expand details textarea height for this line (only one row expanded at a time). */
    function expandLineRow(invoiceLineId) {
        setExpandedDetailsLineId(invoiceLineId);
    }

    const hasValidSelectedLines = useMemo(() => {
        return invoiceLines.some(
            (invoiceLine) =>
                invoiceLine.selected &&
                Number.isInteger(invoiceLine.quantityDesired) &&
                invoiceLine.quantityDesired > 0 &&
                invoiceLine.quantityDesired <= Number(invoiceLine.quantityOnInvoiceLine)
        );
    }, [invoiceLines]);

    const selectedLinesHaveRequiredReasons = useMemo(() => {
        return invoiceLines
            .filter((invoiceLine) => invoiceLine.selected)
            .every((invoiceLine) => invoiceLine.lineReasonForReturn && invoiceLine.lineReasonForReturnText?.trim());
    }, [invoiceLines]);

    const selectedLinesRequiringAttachments = useMemo(() => {
        return invoiceLines.filter(
            (invoiceLine) =>
                invoiceLine.selected &&
                (invoiceLine.lineReasonForReturn === 'Physical Damage' || invoiceLine.lineReasonForReturn === 'Incorrect Part')
        );
    }, [invoiceLines]);

    const selectedLinesHaveRequiredAttachments = useMemo(() => {
        return (
            (selectedLinesRequiringAttachments.length > 0 && attachments.length > 0) ||
            selectedLinesRequiringAttachments.length === 0
        );
    }, [selectedLinesRequiringAttachments, attachments]);

    useEffect(() => {
        const invoiceNumberFromQuery = searchParams.get('invc');
        const releaseNumberFromQuery = searchParams.get('rls');
        const poNumberFromQuery = searchParams.get('po');

        if (!(invoiceNumberFromQuery || releaseNumberFromQuery) || !poNumberFromQuery) {
            setViewState('entry');
            return;
        }

        setInvoiceNumber(invoiceNumberFromQuery);
        setReleaseNumber(releaseNumberFromQuery);
        setPoNumber(poNumberFromQuery);
        setViewState('loading');
        setErrorNotificationVisible(false);
        setIsLoadingValidation(true);

        async function validateAndLoadInvoiceLines() {
            try {
                let invoiceNumberFromRelease;
                if (releaseNumberFromQuery) {
                    const response = await fetch(`${ORIGIN}/rma/validate-release`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            releaseNumber: releaseNumberFromQuery,
                        }),
                    });

                    if (!response.ok) {
                        throw new Error(`We could not validate this request (status ${response.status}).`);
                    }

                    const responseContentType = response.headers.get('content-type') || '';
                    const responseHasJsonBody = responseContentType.includes('application/json');
                    const results = responseHasJsonBody ? await response.json() : null;

                    if (results.status === 'notFound' || results.status === 'invalid') {
                        const validationTitle = results.status === 'notFound' ? 'No matching shipment' : 'Invalid request';
                        presentErrorNotification(validationTitle, results.message ?? errorString);
                        setViewState('entry');
                        navigateToRma();
                        return;
                    }

                    invoiceNumberFromRelease = results?.invoiceNumber?.trim() || '';
                    setInvoiceNumber(invoiceNumberFromRelease);
                }

                const response = await fetch(`${ORIGIN}/rma/validate`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        invoiceNumber: invoiceNumberFromRelease || invoiceNumberFromQuery,
                        poNumber: poNumberFromQuery,
                    }),
                });

                if (!response.ok) {
                    throw new Error(`We could not validate this request (status ${response.status}).`);
                }

                const responseContentType = response.headers.get('content-type') || '';
                const responseHasJsonBody = responseContentType.includes('application/json');
                const results = responseHasJsonBody ? await response.json() : null;

                if (results.status === 'ready') {
                    const mappedInvoiceLines = (results.invoiceLines || []).map((invoiceLine) => ({
                        ...invoiceLine,
                        selected: false,
                        quantityDesired: Number(invoiceLine.quantityOnInvoiceLine) || 0,
                        lineReasonForReturn: '',
                        lineReasonForReturnText: '',
                    }));

                    setOrderDetails(results.orderDetails);
                    setInvoiceLines(mappedInvoiceLines);
                    setLineReturnReasonOptions(results.lineReturnReasonOptions || []);
                    setExpandedDetailsLineId(null);
                    clearAttachments();

                    const contacts = results.contactsOnAccount || {};
                    setContactsOnAccount(contacts);
                    const primaryId = results.orderDetails?.primaryContactId;
                    const initialContactId = primaryId && contacts[primaryId] ? primaryId : Object.keys(contacts)[0] || '';
                    setSelectedContactId(initialContactId);
                    const initialContact = initialContactId ? contacts[initialContactId] : null;
                    if (initialContact) {
                        setRmaContact(contactToRmaForm(initialContact));
                    } else {
                        setRmaContact({
                            name: results.orderDetails?.contactName || '',
                            phone: results.orderDetails?.contactPhone || '',
                            email: results.orderDetails?.contactEmail || '',
                        });
                    }
                    setViewState('lineSelection');
                    return;
                }

                if (results.status === 'alreadyExists') {
                    const replacedMessage = releaseNumberFromQuery
                        ? results.message?.replace(/Invoice/, 'Shipment Reference')
                        : results.message;
                    presentErrorNotification('RMA already exists', replacedMessage ?? errorString);
                    setViewState('entry');
                    if (releaseNumberFromQuery) {
                        setInvoiceNumber('');
                    }
                    navigateToRma();
                    return;
                }

                if (results.status === 'notFound' || results.status === 'invalid') {
                    const validationTitle =
                        results.status === 'notFound'
                            ? `No matching ${releaseNumberFromQuery ? 'Shipment Reference' : 'Invoice'}`
                            : 'Invalid request';
                    const replacedMessage = releaseNumberFromQuery
                        ? results.message?.replace(/Invoice/, 'Shipment Reference')
                        : results.message;
                    presentErrorNotification(validationTitle, replacedMessage ?? errorString);
                    setViewState('entry');
                    if (releaseNumberFromQuery) {
                        setInvoiceNumber('');
                    }
                    navigateToRma();
                    return;
                }

                presentErrorNotification('Validation failed', errorString);
                setViewState('entry');
                navigateToRma();
            } catch (error) {
                presentErrorNotification('Validation failed', errorString);
                setViewState('entry');
                navigateToRma();
            } finally {
                setIsLoadingValidation(false);
            }
        }

        validateAndLoadInvoiceLines();
    }, [ORIGIN, searchParams]);

    function goToInvoiceQueryState(event) {
        event.preventDefault();
        const sanitizedInvoiceNumber = invoiceNumber?.trim();
        const sanitizedReleaseNumber = releaseNumber?.trim();
        const sanitizedPo = poNumber?.trim();

        if (!(sanitizedInvoiceNumber || sanitizedReleaseNumber) || !sanitizedPo) {
            presentErrorNotification(
                'Missing information',
                'Invoice Number/Shipment Reference Number and PO Number are required.'
            );
            navigateToRma();
            return;
        }

        const searchTerm = sanitizedInvoiceNumber ? { invc: sanitizedInvoiceNumber } : { rls: sanitizedReleaseNumber };

        const queryParams = new URLSearchParams({
            ...searchTerm,
            internal: isInternalRmaEntry ? 'true' : 'false',
            po: sanitizedPo,
        });
        navigate(`/rma?${queryParams.toString()}`);
    }

    function handleLineCheckboxChange(invoiceLineId, checked) {
        setInvoiceLines((previousInvoiceLines) =>
            previousInvoiceLines.map((invoiceLine) =>
                invoiceLine.invoiceLineId !== invoiceLineId ? invoiceLine : { ...invoiceLine, selected: checked }
            )
        );
        if (checked) {
            setExpandedDetailsLineId(invoiceLineId);
        }
    }

    function updateDesiredQuantity(invoiceLineId, quantityInput) {
        setInvoiceLines((previousInvoiceLines) =>
            previousInvoiceLines.map((invoiceLine) => {
                if (invoiceLine.invoiceLineId !== invoiceLineId) return invoiceLine;

                const maxQuantity = Number(invoiceLine.quantityOnInvoiceLine) || 0;
                const numericQuantity = Number(quantityInput);
                if (!Number.isFinite(numericQuantity)) {
                    return {
                        ...invoiceLine,
                        quantityDesired: 0,
                    };
                }

                const flooredQuantity = Math.floor(numericQuantity);
                const clampedQuantity = Math.max(0, Math.min(flooredQuantity, maxQuantity));
                return {
                    ...invoiceLine,
                    quantityDesired: clampedQuantity,
                };
            })
        );
    }

    function updateLineReturnReason(invoiceLineId, lineReasonForReturn) {
        setInvoiceLines((previousInvoiceLines) =>
            previousInvoiceLines.map((invoiceLine) => {
                if (invoiceLine.invoiceLineId !== invoiceLineId) return invoiceLine;
                return {
                    ...invoiceLine,
                    lineReasonForReturn,
                };
            })
        );
    }

    function updateLineReturnReasonText(invoiceLineId, lineReasonForReturnText) {
        setInvoiceLines((previousInvoiceLines) =>
            previousInvoiceLines.map((invoiceLine) => {
                if (invoiceLine.invoiceLineId !== invoiceLineId) return invoiceLine;
                return {
                    ...invoiceLine,
                    lineReasonForReturnText,
                };
            })
        );
    }

    async function submitRmaRequest() {
        if (!orderDetails?.invoiceId || !hasValidSelectedLines || !selectedLinesHaveRequiredReasons) return;

        setIsSubmitting(true);

        const selectedLines = invoiceLines
            .filter((invoiceLine) => invoiceLine.selected)
            .map((invoiceLine) => ({
                invoiceLineId: invoiceLine.invoiceLineId,
                quantityDesired: invoiceLine.quantityDesired,
                lineReasonForReturn: invoiceLine.lineReasonForReturn || '',
                lineReasonForReturnText: (invoiceLine.lineReasonForReturnText || '').trim(),
            }));

        try {
            const formData = new FormData();
            formData.append('invoiceNumber', invoiceNumber);
            formData.append('releaseName', releaseNumber);
            formData.append('poNumber', poNumber);
            formData.append('invoiceId', orderDetails.invoiceId);
            formData.append('selectedLines', JSON.stringify(selectedLines));
            formData.append('contactId', selectedContactId);
            formData.append('contactName', rmaContact.name.trim());
            formData.append('contactPhone', rmaContact.phone.trim());
            formData.append('contactEmail', rmaContact.email.trim());
            formData.append('internalRmaOnly', internalRmaOnly ? 'true' : 'false');
            attachments.forEach((attachment) => {
                formData.append('attachments', attachment.file);
            });

            const response = await fetch(`${ORIGIN}/rma/submit`, {
                method: 'POST',
                body: formData,
            });

            const responseContentType = response.headers.get('content-type') || '';
            const responseHasJsonBody = responseContentType.includes('application/json');
            const results = responseHasJsonBody ? await response.json() : null;

            if (!response.ok) {
                throw new Error(results?.message || `We could not submit this RMA request (status ${response.status}).`);
            }

            if (results.status === 'submitted') {
                setErrorNotificationVisible(false);
                setViewState('submitted');
                return;
            }

            if (results.status === 'alreadyExists') {
                presentErrorNotification('RMA already exists', results.message ?? 'We could not submit this RMA request.');
                return;
            }

            presentErrorNotification('Could not submit RMA', results.message ?? 'We could not submit this RMA request.');
        } catch (error) {
            presentErrorNotification('Could not submit RMA', error.message || 'We could not submit this RMA request.');
        } finally {
            setIsSubmitting(false);
        }
    }

    function resetToEntryState() {
        setExpandedDetailsLineId(null);
        setInvoiceNumber('');
        setReleaseNumber('');
        setPoNumber('');
        setOrderDetails(null);
        setInvoiceLines([]);
        clearAttachments();
        setRmaContact({
            name: '',
            phone: '',
            email: '',
        });
        setContactsOnAccount({});
        setSelectedContactId('');
        setErrorNotificationVisible(false);
        setViewState('entry');
        navigateToRma();
    }

    const internalRmaOnlyCheckbox = isInternalRmaEntry ? (
        <label className='flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer'>
            <input
                type='checkbox'
                name='internalRmaOnly'
                value='true'
                checked={internalRmaOnly}
                onChange={(event) => setInternalRmaOnly(event.target.checked)}
                className='w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-400'
            />
            <span>INTERNAL RMA ONLY</span>
        </label>
    ) : null;

    const errorNotification = (
        <Notification
            showSavedNotification={errorNotificationVisible}
            setShowSavedNotification={setErrorNotificationVisible}
            title={errorNotificationTitle}
            desc={errorNotificationDesc}
            status='error'
        />
    );

    if (viewState === 'loading') {
        return (
            <Fragment>
                {errorNotification}
                <div className='flex justify-center items-center px-4 w-full min-h-screen bg-white'>
                    <div className='flex flex-col gap-4 items-center'>
                        <div className='w-10 h-10 rounded-full border-4 border-gray-200 animate-spin border-t-blue-600'></div>
                        <p className='text-gray-700'>{isLoadingValidation ? 'Loading invoice lines...' : 'Loading...'}</p>
                    </div>
                </div>
            </Fragment>
        );
    }

    if (viewState === 'entry') {
        return (
            <Fragment>
                {errorNotification}
                <div className='grid px-4 w-full h-screen bg-gray-50 grid-rows-[35%_10%_27%_28%]'>
                    <div className='flex justify-center items-center'>
                        <img
                            src={fullLogo}
                            alt='Pacific Air Industries'
                            className='object-contain w-auto max-w-full h-full'
                            style={{ maxHeight: '100%' }}
                        />
                    </div>

                    <div className='flex justify-center items-start -mt-16'>
                        <h1 className='text-[clamp(1.5rem,2.5vw,2rem)] font-semibold text-gray-900'>
                            Return Request Portal
                        </h1>
                    </div>

                    <div className='flex justify-center items-center z-10'>
                        <div className='p-6 w-full max-w-2xl bg-white rounded-lg border border-gray-200 shadow-sm'>
                            <h1 className='text-md md:text-lg font-medium text-gray-900'>
                                Enter your PO number and your shipment reference number OR your invoice number below to begin
                                your return authorization request.
                            </h1>
                            <form
                                className='flex flex-col gap-4 justify-center items-center mt-6 w-full'
                                onSubmit={goToInvoiceQueryState}
                            >
                                <div className='flex flex-col gap-4 items-center w-full'>
                                    <div className='w-full flex gap-4 justify-between items-center'>
                                        <div className={`w-full md:w-auto ${releaseNumber?.trim() ? 'opacity-50' : ''}`}>
                                            <label className='block mb-1 text-sm font-medium text-center text-gray-700 md:text-left'>
                                                Invoice Number
                                            </label>
                                            <div className='flex justify-center items-center md:justify-start'>
                                                <span className='mr-1 text-base text-gray-500 select-none'>INVC-</span>
                                                <input
                                                    type='text'
                                                    inputMode='numeric'
                                                    pattern='[0-9]*'
                                                    value={invoiceNumber}
                                                    onChange={(event) => {
                                                        // Only allow numbers
                                                        const val = event.target.value.replace(/\D/g, '');
                                                        setInvoiceNumber(val);
                                                    }}
                                                    className={`px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                                                        releaseNumber?.trim() ? 'cursor-not-allowed' : ''
                                                    }`}
                                                    placeholder='Enter Invoice number'
                                                    disabled={releaseNumber?.trim().length > 0}
                                                    required={!releaseNumber?.trim()}
                                                    style={{ minWidth: '160px' }}
                                                    autoCorrect='off'
                                                    autoCapitalize='off'
                                                    spellCheck={false}
                                                />
                                            </div>
                                        </div>

                                        <span className='text-gray-700 select-none' aria-hidden='true'>
                                            OR
                                        </span>

                                        <div className={`w-full md:w-auto ${invoiceNumber?.trim() ? 'opacity-50' : ''}`}>
                                            <label className='block mb-1 text-sm font-medium text-center text-gray-700 md:text-left'>
                                                Shipment Reference Number
                                            </label>
                                            <div className='flex justify-center items-center md:justify-start'>
                                                <span className='mr-1 text-base text-gray-500 select-none'>RLS-</span>
                                                <input
                                                    type='text'
                                                    inputMode='numeric'
                                                    pattern='[0-9]*'
                                                    value={releaseNumber}
                                                    onChange={(event) => {
                                                        // Only allow numbers
                                                        const val = event.target.value.replace(/\D/g, '');
                                                        setReleaseNumber(val);
                                                    }}
                                                    className={`px-3 py-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                                                        invoiceNumber?.trim() ? 'cursor-not-allowed' : ''
                                                    }`}
                                                    placeholder='Enter Shipment Reference Number'
                                                    disabled={invoiceNumber?.trim().length > 0}
                                                    required={!invoiceNumber?.trim()}
                                                    style={{ minWidth: '160px' }}
                                                    autoCorrect='off'
                                                    autoCapitalize='off'
                                                    spellCheck={false}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className='w-full md:w-auto'>
                                        <label className='block mb-1 text-sm font-medium text-center text-gray-700 md:text-left'>
                                            PO Number
                                        </label>
                                        <input
                                            type='text'
                                            value={poNumber}
                                            onChange={(event) => setPoNumber(event.target.value)}
                                            className='px-3 py-2 w-full rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
                                            placeholder='Enter PO number'
                                            required
                                            style={{ minWidth: '160px' }}
                                            autoCorrect='off'
                                            autoCapitalize='off'
                                            spellCheck={false}
                                        />
                                    </div>
                                </div>
                                <div className='flex justify-center items-start'>
                                    {internalRmaOnlyCheckbox}
                                </div>
                                <button
                                    type='submit'
                                    className='px-5 py-2 mt-2 w-40 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed'
                                    disabled={!(invoiceNumber || releaseNumber) || !poNumber}
                                >
                                    Submit
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }

    return (
        <Fragment>
            {errorNotification}
            <div className='px-4 py-10 w-full min-h-screen bg-gray-50' onClick={() => setExpandedDetailsLineId(null)}>
                <div className='p-6 mx-auto max-w-6xl bg-white rounded-lg border border-gray-200 shadow-sm'>
                    <h1 className='text-2xl font-semibold text-gray-900'>Request RMA</h1>

                    {viewState === 'lineSelection' && (
                        <div className='mt-8'>
                            <div className='grid grid-cols-1 gap-6 mb-6 lg:grid-cols-2'>
                                <div className='p-4 bg-gray-50 rounded border border-gray-200'>
                                    <div className='text-sm text-gray-700'>
                                        <p>
                                            <span className='font-semibold'>Account Name:</span> {orderDetails?.accountName}
                                        </p>
                                        <p>
                                            <span className='font-semibold'>Invoice:</span> {orderDetails?.invoiceNumber}
                                        </p>
                                        <p>
                                            <span className='font-semibold'>PO Number:</span> {orderDetails?.poNumber}
                                        </p>
                                    </div>
                                </div>

                                <div className='p-4 bg-gray-50 rounded border border-gray-200'>
                                    <div className='flex justify-between items-center mb-4'>
                                        <h2 className='text-lg font-semibold text-gray-900'>Contact information</h2>
                                        <div className='flex gap-2 items-center'>
                                            <button
                                                type='button'
                                                className='px-3 py-1 text-sm font-medium text-gray-700 bg-white rounded border border-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60'
                                                onClick={openEditContactModal}
                                                disabled={!selectedContactId}
                                            >
                                                Edit Contact
                                            </button>
                                            <button
                                                type='button'
                                                className='px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700'
                                                onClick={openCreateContactModal}
                                            >
                                                Add New Contact
                                            </button>
                                        </div>
                                    </div>
                                    <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                                        <div className='md:col-span-2'>
                                            <label
                                                htmlFor='rmaContactSelect'
                                                className='block mb-1 text-sm font-medium text-gray-700'
                                            >
                                                Contact
                                            </label>
                                            <select
                                                id='rmaContactSelect'
                                                value={selectedContactId}
                                                onChange={handleRmaContactDropdownChange}
                                                className='px-2 w-full h-10 text-sm bg-white rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
                                                aria-label='Select account contact'
                                            >
                                                <option value=''>{'\u00a0'}</option>
                                                {Object.values(contactsOnAccount).map((contactOption) => (
                                                    <option key={contactOption.Id} value={contactOption.Id}>
                                                        {contactOption.Name || contactOption.Id}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className='md:col-span-2'>
                                            <label
                                                htmlFor='rmaContactName'
                                                className='block mb-1 text-sm font-medium text-gray-700'
                                            >
                                                Name
                                            </label>
                                            <input
                                                id='rmaContactName'
                                                type='text'
                                                value={rmaContact.name}
                                                readOnly
                                                className='px-2 w-full h-10 text-sm rounded border border-gray-300 bg-gray-100 text-gray-700 focus:outline-none'
                                                autoComplete='name'
                                            />
                                        </div>
                                        <div>
                                            <label
                                                htmlFor='rmaContactPhone'
                                                className='block mb-1 text-sm font-medium text-gray-700'
                                            >
                                                Phone
                                            </label>
                                            <input
                                                id='rmaContactPhone'
                                                type='tel'
                                                value={rmaContact.phone}
                                                readOnly
                                                className='px-2 w-full h-10 text-sm rounded border border-gray-300 bg-gray-100 text-gray-700 focus:outline-none'
                                                autoComplete='tel'
                                            />
                                        </div>
                                        <div className='md:col-span-2'>
                                            <label
                                                htmlFor='rmaContactEmail'
                                                className='block mb-1 text-sm font-medium text-gray-700'
                                            >
                                                Email
                                            </label>
                                            <input
                                                id='rmaContactEmail'
                                                type='email'
                                                value={rmaContact.email}
                                                readOnly
                                                className='px-2 w-full h-10 text-sm rounded border border-gray-300 bg-gray-100 text-gray-700 focus:outline-none'
                                                autoComplete='email'
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='overflow-x-auto rounded border border-gray-200'>
                                <table className='min-w-full text-sm'>
                                    <thead className='bg-gray-100'>
                                        <tr>
                                            <th className='px-3 py-2 text-center border-b'>Select</th>
                                            <th className='px-3 py-2 text-left border-b'>Part Number</th>
                                            <th className='px-3 py-2 text-left border-b'>Keyword</th>
                                            <th className='px-3 py-2 text-left border-b'>Serial Number</th>
                                            <th className='px-3 py-2 text-left border-b'>Condition Code</th>
                                            <th className='px-3 py-2 text-center border-b'>
                                                Quantity{' '}
                                                <span className='italic font-normal text-red-500 whitespace-nowrap'>
                                                    *required
                                                </span>
                                            </th>
                                            <th className='px-3 py-2 text-left border-b'>
                                                Line Return Reason{' '}
                                                <span className='italic font-normal text-red-500 whitespace-nowrap'>
                                                    *required
                                                </span>
                                            </th>
                                            <th className='px-3 py-2 text-left border-b'>
                                                Line Return Reason Details{' '}
                                                <span className='italic font-normal text-red-500 whitespace-nowrap'>
                                                    *required
                                                </span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody onClick={(event) => event.stopPropagation()}>
                                        {invoiceLines.map((invoiceLine) => (
                                            <tr
                                                key={invoiceLine.invoiceLineId}
                                                className='odd:bg-white even:bg-gray-50'
                                                onClick={() => {
                                                    if (invoiceLine.selected) {
                                                        expandLineRow(invoiceLine.invoiceLineId);
                                                    }
                                                }}
                                            >
                                                <td className='px-1 py-2 text-center border-b'>
                                                    <input
                                                        type='checkbox'
                                                        checked={invoiceLine.selected}
                                                        onChange={(event) =>
                                                            handleLineCheckboxChange(
                                                                invoiceLine.invoiceLineId,
                                                                event.target.checked
                                                            )
                                                        }
                                                        onClick={(event) => event.stopPropagation()}
                                                        className='w-5 h-5'
                                                    />
                                                </td>
                                                <td className='px-3 py-2 border-b'>{invoiceLine.partNumber}</td>
                                                <td className='px-3 py-2 border-b'>{invoiceLine.keyword}</td>
                                                <td className='px-3 py-2 border-b'>{invoiceLine.serialNumber}</td>
                                                <td className='px-3 py-2 border-b'>{invoiceLine.conditionCode}</td>
                                                <td className='px-3 py-2 text-center border-b'>
                                                    <input
                                                        type='number'
                                                        min='1'
                                                        max={invoiceLine.quantityOnInvoiceLine}
                                                        disabled={!invoiceLine.selected}
                                                        value={invoiceLine.quantityDesired}
                                                        onChange={(event) =>
                                                            updateDesiredQuantity(
                                                                invoiceLine.invoiceLineId,
                                                                event.target.value
                                                            )
                                                        }
                                                        className='px-2 py-1 w-20 text-right rounded border border-gray-300 disabled:bg-gray-100'
                                                    />
                                                    <div className='mt-1 text-xs text-gray-500'>
                                                        Max: {invoiceLine.quantityOnInvoiceLine}
                                                    </div>
                                                </td>
                                                <td className='px-3 py-2 border-b'>
                                                    <select
                                                        value={invoiceLine.lineReasonForReturn}
                                                        onChange={(event) =>
                                                            updateLineReturnReason(
                                                                invoiceLine.invoiceLineId,
                                                                event.target.value
                                                            )
                                                        }
                                                        disabled={!invoiceLine.selected}
                                                        className='px-2 py-1 w-52 max-w-full text-sm bg-white rounded border border-gray-300 disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400'
                                                    >
                                                        <option value=''>Not selected</option>
                                                        {lineReturnReasonOptions.map((lineReturnReasonOption) => (
                                                            <option
                                                                key={lineReturnReasonOption.value}
                                                                value={lineReturnReasonOption.value}
                                                            >
                                                                {lineReturnReasonOption.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td className='px-3 py-2 align-top border-b'>
                                                    <div className='relative'>
                                                        <textarea
                                                            value={invoiceLine.lineReasonForReturnText || ''}
                                                            onChange={(event) =>
                                                                updateLineReturnReasonText(
                                                                    invoiceLine.invoiceLineId,
                                                                    event.target.value.slice(0, 255)
                                                                )
                                                            }
                                                            disabled={!invoiceLine.selected}
                                                            className='px-2 py-1 w-64 max-w-full text-sm rounded border border-gray-300 resize-none disabled:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400'
                                                            rows={
                                                                expandedDetailsLineId === invoiceLine.invoiceLineId
                                                                    ? DETAILS_TEXTAREA_ROWS_EXPANDED
                                                                    : DETAILS_TEXTAREA_ROWS_COLLAPSED
                                                            }
                                                            maxLength={255}
                                                            placeholder='Enter details'
                                                        />
                                                        <span className='absolute right-2 bottom-1 text-[10px] text-gray-500'>
                                                            {(invoiceLine.lineReasonForReturnText || '').length}/255
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className='mt-5'>
                                <label htmlFor='rmaAttachments' className='block mb-2 text-sm font-medium text-gray-700'>
                                    Attachments (PDF, JPG, PNG) - Max 10 Files
                                    {selectedLinesRequiringAttachments.length > 0 && (
                                        <>
                                            {' '}
                                            <span className='italic font-normal text-red-500 whitespace-nowrap'>
                                                *required for selected Line Return Reason(s):{' '}
                                                {[
                                                    ...new Set(
                                                        selectedLinesRequiringAttachments.map(
                                                            (line) => line.lineReasonForReturn
                                                        )
                                                    ),
                                                ]?.join(' and ')}
                                            </span>
                                        </>
                                    )}
                                </label>
                                <input
                                    ref={attachmentInputRef}
                                    id='rmaAttachments'
                                    name='rmaAttachments'
                                    type='file'
                                    multiple
                                    accept='.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png'
                                    onChange={handleAttachmentChange}
                                    className='hidden'
                                />
                                <button
                                    type='button'
                                    onClick={() => attachmentInputRef.current?.click()}
                                    className='px-4 py-2 mr-4 text-sm font-medium text-white bg-blue-600 rounded cursor-pointer hover:bg-blue-700'
                                >
                                    Choose Files
                                </button>
                                {attachments.length > 0 && (
                                    <Fragment>
                                        <div className='grid grid-cols-1 gap-2 mt-3 sm:grid-cols-2 lg:grid-cols-3'>
                                            {attachments.map((attachment) => (
                                                <div
                                                    key={attachment.id}
                                                    className='flex gap-2 items-center p-2 bg-white rounded border border-gray-200'
                                                >
                                                    <div className='flex overflow-hidden justify-center items-center w-12 h-12 text-xs font-semibold text-gray-700 bg-gray-100 rounded border border-gray-200'>
                                                        {attachment.isImageAttachment ? (
                                                            <img
                                                                src={attachment.previewUrl}
                                                                alt={attachment.file.name}
                                                                className='object-cover w-full h-full'
                                                            />
                                                        ) : (
                                                            <span>PDF</span>
                                                        )}
                                                    </div>
                                                    <div className='flex-1 min-w-0'>
                                                        <p
                                                            className='text-xs text-gray-800 truncate'
                                                            title={attachment.file.name}
                                                        >
                                                            {attachment.file.name}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type='button'
                                                        onClick={() => removeAttachment(attachment.id)}
                                                        className='px-2 py-1 text-xs text-red-600 rounded border border-red-200 hover:bg-red-50'
                                                        aria-label={`Remove ${attachment.file.name}`}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </Fragment>
                                )}
                            </div>

                            <div
                                className={`flex gap-3 items-center mt-5 ${
                                    isInternalRmaEntry ? 'justify-between' : 'justify-end'
                                }`}
                            >
                                {internalRmaOnlyCheckbox}
                                <button
                                    className='px-5 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed'
                                    disabled={
                                        !hasValidSelectedLines ||
                                        !selectedLinesHaveRequiredReasons ||
                                        !selectedLinesHaveRequiredAttachments ||
                                        isSubmitting
                                    }
                                    onClick={submitRmaRequest}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit RMA'}
                                </button>
                            </div>
                        </div>
                    )}

                    {viewState === 'submitted' && (
                        <div className='mt-10 text-center'>
                            <h2 className='text-3xl font-semibold text-gray-900'>Your request has been received.</h2>
                            {!internalRmaOnly && (
                                <p className='mt-4 text-gray-600'>
                                    We will respond to your RMA request within the next 10 business days via email.
                                </p>
                            )}
                            <button
                                className='px-5 py-2 mt-8 text-white bg-blue-600 rounded hover:bg-blue-700'
                                onClick={resetToEntryState}
                            >
                                File Another RMA
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {isCreateContactModalOpen && (
                <div className='flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/40'>
                    <div className='p-5 w-full max-w-md bg-white rounded-lg border border-gray-200 shadow-xl'>
                        <h3 className='text-lg font-semibold text-gray-900'>Add New Contact</h3>
                        <div className='grid grid-cols-1 gap-3 mt-4'>
                            <div>
                                <label htmlFor='rmaNewContactName' className='block mb-1 text-sm font-medium text-gray-700'>
                                    Name
                                </label>
                                <input
                                    id='rmaNewContactName'
                                    type='text'
                                    value={newContactForm.name}
                                    onChange={(event) => updateNewContactField('name', event.target.value)}
                                    className='px-2 w-full h-10 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
                                    autoComplete='name'
                                />
                            </div>
                            <div>
                                <label htmlFor='rmaNewContactPhone' className='block mb-1 text-sm font-medium text-gray-700'>
                                    Phone
                                </label>
                                <input
                                    id='rmaNewContactPhone'
                                    type='tel'
                                    value={newContactForm.phone}
                                    onChange={(event) => updateNewContactField('phone', event.target.value)}
                                    className='px-2 w-full h-10 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
                                    autoComplete='tel'
                                />
                            </div>
                            <div>
                                <label htmlFor='rmaNewContactEmail' className='block mb-1 text-sm font-medium text-gray-700'>
                                    Email
                                </label>
                                <input
                                    id='rmaNewContactEmail'
                                    type='email'
                                    value={newContactForm.email}
                                    onChange={(event) => updateNewContactField('email', event.target.value)}
                                    className='px-2 w-full h-10 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
                                    autoComplete='email'
                                    required
                                />
                            </div>
                        </div>
                        <div className='flex gap-2 justify-end mt-5'>
                            <button
                                type='button'
                                className='px-4 py-2 text-sm text-gray-700 rounded border border-gray-300 hover:bg-gray-100'
                                onClick={closeCreateContactModal}
                            >
                                Cancel
                            </button>
                            <button
                                type='button'
                                className='px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed'
                                onClick={createContactFromModal}
                            >
                                Save Contact
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isEditContactModalOpen && (
                <div className='flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/40'>
                    <div className='p-5 w-full max-w-md bg-white rounded-lg border border-gray-200 shadow-xl'>
                        <h3 className='text-lg font-semibold text-gray-900'>Edit Contact</h3>
                        <div className='grid grid-cols-1 gap-3 mt-4'>
                            <div>
                                <label htmlFor='rmaEditContactName' className='block mb-1 text-sm font-medium text-gray-700'>
                                    Name
                                </label>
                                <input
                                    id='rmaEditContactName'
                                    type='text'
                                    value={editContactForm.name}
                                    onChange={(event) => updateEditContactField('name', event.target.value)}
                                    className='px-2 w-full h-10 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
                                    autoComplete='name'
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor='rmaEditContactPhone'
                                    className='block mb-1 text-sm font-medium text-gray-700'
                                >
                                    Phone
                                </label>
                                <input
                                    id='rmaEditContactPhone'
                                    type='tel'
                                    value={editContactForm.phone}
                                    onChange={(event) => updateEditContactField('phone', event.target.value)}
                                    className='px-2 w-full h-10 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400'
                                    autoComplete='tel'
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor='rmaEditContactEmail'
                                    className='block mb-1 text-sm font-medium text-gray-700'
                                >
                                    Email
                                </label>
                                <input
                                    id='rmaEditContactEmail'
                                    type='email'
                                    value={editContactForm.email}
                                    disabled
                                    className='px-2 w-full h-10 text-sm rounded border border-gray-300 bg-gray-100 text-gray-700'
                                    autoComplete='email'
                                />
                            </div>
                        </div>
                        <div className='flex gap-2 justify-end mt-5'>
                            <button
                                type='button'
                                className='px-4 py-2 text-sm text-gray-700 rounded border border-gray-300 hover:bg-gray-100'
                                onClick={closeEditContactModal}
                            >
                                Cancel
                            </button>
                            <button
                                type='button'
                                className='px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700'
                                onClick={saveEditedContactFromModal}
                            >
                                Save Contact
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}
